import { EmptyState } from "@/components/common/EmptyState";
import { IntentEditor } from "@/components/common/IntentEditor";
import { PageHeader } from "@/components/layout/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useMembers, useOrg, useTeamMutations, useTeams } from "@/hooks";
import { intentSummary } from "@/lib/intent";
import { ApiError } from "@/services";
import type { OrgIntent, OrgMember, OrgTeam } from "@/types/api";
import { Plus, X } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Teams: the groups inside an organization, and what each is working towards.
 *
 * WHAT THIS DOES NOT DO, said plainly on the screen as well as here: nothing
 * reads a team's intent yet. It is captured now so that when planning does read
 * it, a partner's answer is already there — but a dashboard that implied
 * tomorrow's feed would change is making a promise the backend does not keep,
 * and the partner would find that out themselves.
 *
 * A person belongs to at most one team, which is why assignment is a move
 * rather than a checkbox: adding someone to a second team takes them out of the
 * first, and the UI should not suggest otherwise.
 */
export function TeamsPage() {
	const org = useOrg();
	const { data, isLoading } = useTeams({ includeMembers: true });
	const [creating, setCreating] = useState(false);
	const [editing, setEditing] = useState<OrgTeam | null>(null);

	// Only an admin may change anything. A member cannot open this page
	// (ModuleRoute); canEdit stays as a second guard, should a route change ever
	// let one in, so they would get it read-only rather than buttons that 403.
	const canEdit = org.data?.org.role !== "member";
	const teams = data?.teams ?? [];

	return (
		<>
			<PageHeader
				title="Teams"
				description="Group people by what they do, and say what each group is working towards."
				action={
					canEdit ? (
						<Button onClick={() => setCreating(true)}>
							<Plus className="mr-1.5 h-4 w-4" />
							New team
						</Button>
					) : null
				}
			/>

			{isLoading ? (
				<Skeleton className="h-40 w-full" />
			) : teams.length === 0 ? (
				<EmptyState
					title="No teams yet"
					description="Teams let you say what different parts of your organization are trying to learn — your tax team and your support team rarely want the same thing."
					action={
						canEdit ? (
							<Button onClick={() => setCreating(true)}>Create a team</Button>
						) : null
					}
				/>
			) : (
				<div className="flex flex-col gap-3">
					{teams.map((team) => (
						<TeamCard
							key={team.id}
							team={team}
							canEdit={canEdit}
							onEdit={() => setEditing(team)}
						/>
					))}
				</div>
			)}

			<TeamDialog open={creating} onOpenChange={setCreating} team={null} />
			<TeamDialog
				open={!!editing}
				onOpenChange={(o) => !o && setEditing(null)}
				team={editing}
			/>
		</>
	);
}

function TeamCard({
	team,
	canEdit,
	onEdit,
}: { team: OrgTeam; canEdit: boolean; onEdit: () => void }) {
	const [managing, setManaging] = useState(false);
	const members = team.members ?? [];
	const summary = intentSummary(team.intent);

	return (
		<Card>
			<CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
				<div className="min-w-0">
					<h2 className="text-body font-semibold text-heading">{team.name}</h2>
					{team.description ? (
						<p className="mt-0.5 text-caption text-muted-foreground">
							{team.description}
						</p>
					) : null}

					{summary ? (
						<p className="mt-2 max-w-xl text-caption text-muted-foreground">
							{summary}
						</p>
					) : (
						// Naming the absence, because an empty team is the state we
						// most want an admin to act on.
						<p className="mt-2 text-caption text-muted-foreground">
							Nothing stated yet.
						</p>
					)}

					<p className="mt-3 text-caption text-muted-foreground">
						{members.length === 0
							? "No one on this team"
							: `${members.length} ${members.length === 1 ? "person" : "people"}`}
					</p>
				</div>

				{canEdit ? (
					<div className="flex shrink-0 gap-2">
						<Button variant="outline" onClick={() => setManaging(true)}>
							People
						</Button>
						<Button variant="outline" onClick={onEdit}>
							Edit
						</Button>
						<ArchiveButton team={team} />
					</div>
				) : null}
			</CardContent>

			<MembersDialog open={managing} onOpenChange={setManaging} team={team} />
		</Card>
	);
}

function ArchiveButton({ team }: { team: OrgTeam }) {
	const [open, setOpen] = useState(false);
	const { archive } = useTeamMutations();

	return (
		<>
			<Button variant="outline" onClick={() => setOpen(true)}>
				Archive
			</Button>
			<AlertDialog open={open} onOpenChange={setOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Archive {team.name}?</AlertDialogTitle>
						<AlertDialogDescription>
							It stops being one of the choices. Nobody loses their seat or
							their history, and the team stays on record — this is not a
							delete.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								archive.mutate(team.id, { onSuccess: () => setOpen(false) })
							}
						>
							{archive.isPending ? "Archiving…" : "Archive"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

/** Create when `team` is null, otherwise edit. One dialog, because the fields
 *  and their rules are identical and two would drift. */
function TeamDialog({
	open,
	onOpenChange,
	team,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	team: OrgTeam | null;
}) {
	const { create, update } = useTeamMutations();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [intent, setIntent] = useState<OrgIntent>({});
	const [error, setError] = useState<string | null>(null);

	// Re-seed each time it OPENS, so reopening after a cancel never shows the
	// abandoned draft. Same shape as BuySeatsDialog.
	useEffect(() => {
		if (!open) return;
		setName(team?.name ?? "");
		setDescription(team?.description ?? "");
		setIntent(team?.intent ?? {});
		setError(null);
	}, [open, team]);

	const pending = create.isPending || update.isPending;

	const submit = () => {
		setError(null);
		const onError = (err: unknown) =>
			setError(
				err instanceof ApiError ? err.message : "Something went wrong saving.",
			);
		const done = { onSuccess: () => onOpenChange(false), onError };

		if (team) {
			// intent is REPLACED, not merged — sending only what changed would
			// clear the rest. See teams.service.ts.
			update.mutate(
				{
					teamId: team.id,
					patch: { name, description: description || null, intent },
				},
				done,
			);
			return;
		}
		create.mutate(
			{ slug: slugify(name), name, description: description || null, intent },
			done,
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{team ? `Edit ${team.name}` : "New team"}</DialogTitle>
					<DialogDescription>
						What this team is working towards is stored for planning. It does
						not change anyone's feed today.
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-5">
					<div className="flex flex-col gap-2">
						<span className="text-caption font-medium text-heading">Name</span>
						<Input
							value={name}
							autoFocus
							placeholder="Tax & Compliance"
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
					<div className="flex flex-col gap-2">
						<span className="text-caption font-medium text-heading">
							Description{" "}
							<span className="text-muted-foreground">(optional)</span>
						</span>
						<Input
							value={description}
							placeholder="Closes the books and files"
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					<IntentEditor
						value={intent}
						onChange={setIntent}
						disabled={pending}
					/>
				</div>

				{error ? <p className="text-caption text-error">{error}</p> : null}

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={!name.trim() || pending}>
						{pending ? "Saving…" : team ? "Save" : "Create team"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/**
 * Who is on this team.
 *
 * The roster is the source of candidates, and each seat carries its own
 * `team_id` — so someone already on ANOTHER team is offered as a move, named as
 * such, rather than appearing free.
 */
function MembersDialog({
	open,
	onOpenChange,
	team,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	team: OrgTeam;
}) {
	const { data } = useMembers();
	// The SAME options the page uses: a different argument is a different cache
	// key, and this dialog would fetch the whole list again to read a name.
	const { data: teamsData } = useTeams({ includeMembers: true });
	const { addMember, removeMember } = useTeamMutations();

	const roster = (data?.members ?? []).filter(
		(m) => m.seat_status !== "deactivated",
	);
	const onTeam = roster.filter((m) => m.team_id === team.id);
	const elsewhere = roster.filter((m) => m.team_id !== team.id);
	const teamName = (id: string | null) =>
		teamsData?.teams.find((t) => t.id === id)?.name ?? null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{team.name}</DialogTitle>
					<DialogDescription>
						Someone belongs to one team at a time, so adding them here takes
						them off any other.
					</DialogDescription>
				</DialogHeader>

				<Section title={`On this team (${onTeam.length})`}>
					{onTeam.length === 0 ? (
						<p className="text-caption text-muted-foreground">Nobody yet.</p>
					) : (
						onTeam.map((m) => (
							<Row key={m.id} member={m}>
								<Button
									variant="outline"
									aria-label={`Remove ${m.email}`}
									onClick={() =>
										removeMember.mutate({ teamId: team.id, memberId: m.id })
									}
								>
									<X className="h-4 w-4" />
								</Button>
							</Row>
						))
					)}
				</Section>

				<Section title="Everyone else">
					{elsewhere.length === 0 ? (
						<p className="text-caption text-muted-foreground">
							Everyone with a seat is already on this team.
						</p>
					) : (
						elsewhere.map((m) => (
							<Row key={m.id} member={m} currentTeam={teamName(m.team_id)}>
								<Button
									variant="outline"
									onClick={() =>
										addMember.mutate({
											teamId: team.id,
											member: { memberId: m.id },
										})
									}
								>
									{m.team_id ? "Move here" : "Add"}
								</Button>
							</Row>
						))
					)}
				</Section>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Done
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function Section({
	title,
	children,
}: { title: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-2">
			<h3 className="text-caption font-medium text-heading">{title}</h3>
			<div className="flex flex-col gap-1.5">{children}</div>
		</div>
	);
}

function Row({
	member,
	currentTeam,
	children,
}: {
	member: OrgMember;
	currentTeam?: string | null;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-center justify-between gap-3 rounded-md border border-divider px-3 py-2">
			<div className="min-w-0">
				<p className="truncate text-caption text-heading">
					{member.user_name ?? member.email}
				</p>
				<p className="truncate text-caption text-muted-foreground">
					{/* An invited seat has no account yet, and an admin needs to see
					    that rather than wonder why the name is missing. */}
					{member.seat_status === "invited"
						? `${member.email} · invited`
						: member.email}
					{currentTeam ? ` · on ${currentTeam}` : ""}
				</p>
			</div>
			<div className="shrink-0">{children}</div>
		</div>
	);
}

/** A url-safe key from the name, matching the Gateway's slug rule. The admin
 *  never sees or types it — it exists so the API has a stable handle. */
function slugify(name: string): string {
	const base = name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 63);
	// The rule requires 2-63 chars starting alphanumeric; a name of only
	// punctuation would otherwise produce something the server rejects with an
	// error about a field the admin cannot see.
	return base.length >= 2 ? base : `team-${Date.now().toString(36)}`;
}
